import React, { useEffect, useMemo, useState } from 'react';
import { ArrowUpRight, ChevronLeft, ChevronRight, Loader2, Newspaper, Search, X } from 'lucide-react';
import { Layout } from './Home';
import { fetchNews } from '../services/api';

const CATEGORIES = [
  { key: 'all', label: 'All' },
  { key: 'stocks', label: 'Stocks' },
  { key: 'oil', label: 'Oil' },
  { key: 'commodities', label: 'Commodities' },
  { key: 'business', label: 'Business' },
];

const PAGE_SIZE = 9;

const formatDate = (raw) => {
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const CategoryBadge = ({ category }) => {
  const styles = {
    stocks: 'bg-blue-100 text-blue-800',
    oil: 'bg-amber-100 text-amber-900',
    commodities: 'bg-yellow-100 text-yellow-900',
    business: 'bg-emerald-100 text-emerald-900',
  };
  return (
    <span
      className={`inline-block text-[10px] font-semibold uppercase tracking-widest px-2.5 py-1 rounded-full ${
        styles[category] || 'bg-gray-100 text-gray-800'
      }`}
    >
      {category}
    </span>
  );
};

const NewsCard = ({ item, featured }) => (
  <a
    href={item.link}
    target="_blank"
    rel="noreferrer"
    className={`group block bg-white rounded-2xl overflow-hidden border border-black/5 shadow-sm hover:shadow-xl transition-all duration-300 ${
      featured ? 'md:col-span-2 md:row-span-2' : ''
    }`}
  >
    <div className={`relative overflow-hidden ${featured ? 'aspect-[16/9]' : 'aspect-[16/10]'}`}>
      <img
        src={item.image}
        alt={item.title}
        loading="lazy"
        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        onError={(e) => {
          e.currentTarget.src =
            'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=800&q=80';
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
      <div className="absolute top-4 left-4">
        <CategoryBadge category={item.category} />
      </div>
    </div>
    <div className="p-5">
      <div className="flex items-center justify-between text-xs text-[#0b1a2e]/60 mb-2">
        <span className="font-semibold tracking-wide text-[#0f5c3d]">
          {formatDate(item.published)}
        </span>
        {item.source && <span className="truncate ml-2">{item.source}</span>}
      </div>
      <h3
        className={`font-semibold leading-snug text-[#0b1a2e] line-clamp-2 group-hover:text-[#0f5c3d] transition-colors ${
          featured ? 'text-2xl' : 'text-[17px]'
        }`}
      >
        {item.title}
      </h3>
      {item.summary && (
        <p className="mt-2 text-sm text-[#0b1a2e]/60 line-clamp-3">{item.summary}</p>
      )}
      <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-[#0f5c3d]">
        Read on {item.source || 'source'} <ArrowUpRight className="w-4 h-4" />
      </div>
    </div>
  </a>
);

const NewsPage = () => {
  const [active, setActive] = useState('all');
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch when tab changes
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPage(1);
    fetchNews(active, 50)
      .then((data) => {
        if (!cancelled) setItems(data);
      })
      .catch(() => {
        if (!cancelled) setError('Unable to load news. Please try again shortly.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [active]);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim().toLowerCase()), 250);
    return () => clearTimeout(t);
  }, [query]);

  // Reset page when search changes
  useEffect(() => {
    setPage(1);
  }, [debounced]);

  const filtered = useMemo(() => {
    if (!debounced) return items;
    return items.filter(
      (i) =>
        i.title.toLowerCase().includes(debounced) ||
        (i.summary || '').toLowerCase().includes(debounced) ||
        (i.source || '').toLowerCase().includes(debounced),
    );
  }, [items, debounced]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * PAGE_SIZE;
  const pageItems = filtered.slice(start, start + PAGE_SIZE);
  const [featured, ...rest] = pageItems;

  return (
    <Layout>
      <section className="bg-[#e2ede6] pt-14 pb-20 lg:pt-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
            <div>
              <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.25em] text-[#0f5c3d] uppercase">
                <Newspaper className="w-4 h-4" /> Global Insights
              </span>
              <h1
                className="mt-3 text-5xl lg:text-6xl text-[#0b1a2e] leading-[1.02]"
                style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}
              >
                News & Market Intelligence
              </h1>
              <p className="mt-4 text-[#0b1a2e]/70 max-w-2xl leading-relaxed">
                Real-time headlines curated from trusted global sources across stocks,
                oil, and commodities.
              </p>
            </div>

            {/* Search */}
            <div className="relative w-full md:w-80">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0b1a2e]/50" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search headlines…"
                className="w-full bg-white border border-[#0f5c3d]/15 rounded-full pl-11 pr-10 py-3 text-sm outline-none focus:ring-2 focus:ring-[#0f5c3d]/30"
              />
              {query && (
                <button
                  onClick={() => setQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-[#0f5c3d]/10 hover:bg-[#0f5c3d]/20 text-[#0f5c3d] flex items-center justify-center"
                  aria-label="clear search"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap gap-2 mb-10 border-b border-[#0f5c3d]/20 pb-1">
            {CATEGORIES.map((c) => {
              const isActive = c.key === active;
              return (
                <button
                  key={c.key}
                  onClick={() => setActive(c.key)}
                  className={`relative px-4 py-2.5 text-sm font-medium transition-colors ${
                    isActive
                      ? 'text-[#0f5c3d]'
                      : 'text-[#0b1a2e]/70 hover:text-[#0b1a2e]'
                  }`}
                >
                  {c.label}
                  {isActive && (
                    <span className="absolute left-3 right-3 -bottom-[5px] h-0.5 bg-[#0f5c3d] rounded-full" />
                  )}
                </button>
              );
            })}
          </div>

          {/* Result summary */}
          {!loading && !error && (
            <div className="text-sm text-[#0b1a2e]/60 mb-6">
              Showing{' '}
              <span className="font-semibold text-[#0b1a2e]">
                {pageItems.length}
              </span>{' '}
              of{' '}
              <span className="font-semibold text-[#0b1a2e]">
                {filtered.length}
              </span>{' '}
              article{filtered.length === 1 ? '' : 's'}
              {debounced && (
                <>
                  {' '}for “<span className="text-[#0f5c3d]">{debounced}</span>”
                </>
              )}
            </div>
          )}

          {/* Content */}
          {loading && (
            <div className="flex items-center justify-center py-24 text-[#0b1a2e]/60">
              <Loader2 className="w-6 h-6 animate-spin mr-3" /> Loading latest articles…
            </div>
          )}

          {!loading && error && (
            <div className="bg-white border border-red-200 text-red-800 p-6 rounded-xl">
              {error}
            </div>
          )}

          {!loading && !error && filtered.length === 0 && (
            <div className="bg-white border border-black/5 p-10 rounded-xl text-center text-[#0b1a2e]/60">
              No articles match your search.
            </div>
          )}

          {!loading && !error && filtered.length > 0 && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr">
                {featured && <NewsCard item={featured} featured />}
                {rest.map((item) => (
                  <NewsCard key={item.id} item={item} />
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-12 flex items-center justify-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={safePage <= 1}
                    className="inline-flex items-center gap-1 px-3 py-2 rounded-full border border-[#0f5c3d]/20 text-sm text-[#0b1a2e] hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-4 h-4" /> Previous
                  </button>
                  <div className="flex items-center gap-1 mx-2">
                    {Array.from({ length: totalPages }).map((_, idx) => {
                      const p = idx + 1;
                      // Show first, last, current and neighbors
                      const isEdge = p === 1 || p === totalPages;
                      const isNear = Math.abs(p - safePage) <= 1;
                      if (!isEdge && !isNear) {
                        if (p === safePage - 2 || p === safePage + 2) {
                          return (
                            <span key={p} className="px-1 text-[#0b1a2e]/40">
                              …
                            </span>
                          );
                        }
                        return null;
                      }
                      return (
                        <button
                          key={p}
                          onClick={() => setPage(p)}
                          className={`min-w-[36px] h-9 rounded-full text-sm font-medium transition-colors ${
                            p === safePage
                              ? 'bg-[#0f5c3d] text-white'
                              : 'text-[#0b1a2e] hover:bg-white'
                          }`}
                        >
                          {p}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={safePage >= totalPages}
                    className="inline-flex items-center gap-1 px-3 py-2 rounded-full border border-[#0f5c3d]/20 text-sm text-[#0b1a2e] hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Next <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default NewsPage;
